import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button, DataTable } from "./ui";
import { MDBContainer, MDBRow, MDBCol, MDBTooltip } from "mdb-react-ui-kit";
import { getImageUrl } from "../utils/utils";
import { api, setTenantApiScope } from "../services";
import AddInventoryModal from "./Modals/AddInventoryModel";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Toolbar from "./Toolbar";
import { useTranslation } from "react-i18next";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import { DEFAULT_TABLE_PAGE_SIZE, estimateColumnMinWidth } from "./ui/tableUtils";
import { TableCellEllipsis } from "./ui/TableCell";
import NativeInput from "./NativeInput";
import ProductPriceWithApps from "./ProductPriceWithApps";
import { fetchPricedAppSources, getUniqueConnectedDeliveryApps } from "../utils/productAppPrice";
import { toast } from "react-toastify";
import { resolveInventoryPageError } from "../utils/translateInventoryApiMessage";
import "./inventory.css";

/** Maps DataTable column ids to GET /branches/.../products-with-inventory sortBy fields */
const INVENTORY_TABLE_SORT_FIELDS = {
  name: "name",
  category: "category",
  price: "price",
  current_stock: "current_stock",
  low_stock_threshold: "low_stock_threshold",
  updatedAt: "updatedAt",
};

function InventoryPage() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const { can } = usePermission();
  const canEnableDisableInventory = can(
    RBAC_MODULES.INVENTORY,
    PERMISSION_ACTIONS.ENABLE_DISABLE_INVENTORY
  );
  const canMutateInventory = can(RBAC_MODULES.INVENTORY, PERMISSION_ACTIONS.EDIT);
  const enableDisableInventoryTip = t("inventoryPage.tooltips.noPermissionEnableDisable");
  const mutateInventoryTip = t("inventoryPage.tooltips.noPermissionCreateUpdate");

  const productDisplayName = (item) => {
    const fallback = t("inventoryPage.errors.productFallback");
    if (!item?.product?.name) return fallback;
    if (typeof item.product.name === "string") return item.product.name;
    return isArabic
      ? item.product.name.ar || item.product.name.en || fallback
      : item.product.name.en || item.product.name.ar || fallback;
  };

  const showInventoryError = (err, fallbackKey, productName) => {
    toast.error(resolveInventoryPageError(err, t, fallbackKey, productName));
  };

  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [stores, setStores] = useState([]); // For storing all stores
  const [branches, setBranches] = useState([]); // For storing all branches from all stores
  const [selectedBranch, setSelectedBranch] = useState(null); // Selected branch object
  const [selectedBranchId, setSelectedBranchId] = useState(null); // Selected branch ID
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  // Server-side status filter results (across all branches)
  const [statusResults, setStatusResults] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  // Unified results when search and/or status filter are active
  const [combinedResults, setCombinedResults] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pagination, setPagination] = useState({
    totalInventory: 0,
    totalPages: 1,
    currentPage: 1,
  });
  // Track branch pagination separately, so we can restore it when search clears
  const [branchPagination, setBranchPagination] = useState({
    totalInventory: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [activeTab, setActiveTab] = useState("All"); // For filtering by status
  const [showModal, setShowModal] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingRow, setEditingRow] = useState(null); // Track which row is being edited
  const [editingData, setEditingData] = useState({}); // Store edited values for each row
  const [savingRowId, setSavingRowId] = useState(null); // Track which row is being saved
  const [selectedIds, setSelectedIds] = useState([]);
  const autoSelectedRef = useRef(false);
  const [inventoryEnabled, setInventoryEnabled] = useState(false);
  const [tableSortColumn, setTableSortColumn] = useState("updatedAt");
  const [inventorySortField, setInventorySortField] = useState("updatedAt");
  const [inventorySortDirection, setInventorySortDirection] = useState("desc");
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [apps, setApps] = useState([]);
  const [addonApps, setAddonApps] = useState([]);
  const deliveryApps = useMemo(
    () => getUniqueConnectedDeliveryApps(apps, addonApps),
    [apps, addonApps]
  );

  useEffect(() => {
    fetchStores(); // Fetch all stores and branches
    fetchApps();
  }, []);

  useEffect(() => {
    if (!branches || branches.length === 0) return;
    const updated = branches.map((branch) => {
      const localized = isArabic
        ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
        : branch?.name?.en || branch?.name;
      const storePart = branch.storeName ? ` - (${branch.storeName})` : "";
      return {
        ...branch,
        displayName: localized ? `${localized}${storePart}` : branch.displayName,
      };
    });
    setBranches(updated);
  }, [isArabic]);

  useEffect(() => {
    if (branches.length > 0 && !selectedBranch && !autoSelectedRef.current) {
      // Auto-select first branch only once on initial load
      const firstBranch = branches[0];
      autoSelectedRef.current = true;
      setSelectedBranch(firstBranch);
      setSelectedBranchId(firstBranch._id);
      setTenantApiScope(firstBranch.storeId || firstBranch.store_id || null, firstBranch._id);
      // Don't call fetchInventoryByBranch here - let the branch change useEffect handle it
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches]);

  // Fetch inventory when branch or date changes
  useEffect(() => {
    if (selectedBranchId) {
      fetchInventoryByBranch(selectedBranchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, selectedDate]);

  // Debounce and store search query only (no API calls yet)
  useEffect(() => {
    const trimmed = (searchQuery || "").trim();
    const handle = setTimeout(() => {
      setDebouncedSearchQuery(trimmed);
    }, 500);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  useEffect(() => {
    setInventoryEnabled(!!(selectedBranch && selectedBranch.inventory_enabled));
  }, [selectedBranch]);

  // Log the debounced query whenever it updates
  useEffect(() => {
   
  }, [debouncedSearchQuery]);

  // Fetch combined results when search query changes
  useEffect(() => {
    const q = (debouncedSearchQuery || "").trim();
    const hasFilter = activeTab && activeTab !== "All";
    if (!q && !hasFilter) {
      // No search or filter -> clear combined and restore branch pagination
      setCombinedResults([]);
      setListLoading(false);
      setPagination(branchPagination);
      return;
    }
    // Reset to page 1 for new query or when combining with filter
    setListLoading(true);
    fetchCombinedResults(1);
  }, [debouncedSearchQuery]);

  // Fetch combined results when status tab changes
  useEffect(() => {
    const hasFilter = activeTab && activeTab !== "All";
    const q = (debouncedSearchQuery || "").trim();
    if (!hasFilter && !q) {
      // No filter and no search -> clear combined and restore branch pagination
      setCombinedResults([]);
      setListLoading(false);
      setPagination(branchPagination);
      return;
    }
    setListLoading(true);
    fetchCombinedResults(1);
  }, [activeTab, debouncedSearchQuery, branchPagination]);

  // Keep filteredInventory in sync with inventory (client-side filtering removed)
  useEffect(() => {
    setFilteredInventory(inventory);
  }, [inventory]);

  // Client-side status filtering/sorting removed; server-side filter is used when activeTab changes

  // Transform products (from global search) into inventory row shape used by the table
  const transformProductsToInventory = (products = []) => {
   
    return products.map((product) => {
      const hasInv = product?.inv_found && product?.inventory;
      const category = product?.category_id
        ? {
            nameAr: product.category_id.name?.ar || "",
            nameEn: product.category_id.name?.en || "",
          }
        : null;

      const branchId = product?.branch?._id || product?.inventory?.branch_id || null;
      const branchName = product?.branch?.name || product?.inventory?.branch?.name || product?.inventory?.branch_name || "";

      return {
        _id: hasInv ? product.inventory._id : null,
        product: {
          _id: product?._id,
          product_id: product?.product_id,
          name: product?.name,
          description: product?.description,
          price: product?.price,
          app_prices: product?.app_prices,
          image_path: product?.image_path,
          category,
        },
        branch: {
          _id: branchId,
          name: branchName,
        },
        date: hasInv ? product.inventory.date : selectedDate ? new Date(selectedDate) : new Date(),
        quantity: hasInv ? product.inventory.quantity : 0,
        initial_stock: hasInv ? product.inventory.initial_stock : 0,
        current_stock: hasInv ? product.inventory.current_stock : 0,
        low_stock_threshold: hasInv ? (product.inventory.low_stock_threshold ?? 5) : 0,
        is_below_threshold: hasInv ? !!product.inventory.is_below_threshold : true,
        inv_found: hasInv ? true : false,
        updatedAt: hasInv ? product.inventory.updatedAt || product.inventory.updated_at || null : null,
      };
    });
  };

  const handleSearch = (e) => {
    const val = e.target.value;
 
    setSearchQuery(val);
  };

  // Simple fetcher for global products-with-inventory search
  const fetchSearchResults = async (
    productName,
    page = 1,
    limit = itemsPerPage
  ) => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("productName", productName);
      params.set("page", String(page));
      params.set("limit", String(limit));

      const response = await api.get(
        `/branches/all/products-with-inventory?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Normalize and store only the products array
      const raw = response?.data;
      const products = Array.isArray(raw?.data?.products)
        ? raw.data.products
        : Array.isArray(raw?.products)
        ? raw.products
        : Array.isArray(raw)
        ? raw
        : [];

      setSearchResults(products);
      
      // Derive pagination meta from response for search mode
      const meta =
        (raw && raw.data && raw.data.pagination) ||
        raw?.pagination || {};

      const totalItems =
        meta.totalItems ??
        meta.total ??
        (raw && raw.data && raw.data.totalProducts) ??
        (raw && raw.data && raw.data.total) ??
        products.length;

      const totalPages =
        meta.totalPages ??
        meta.pages ??
        meta.total_pages ??
        (totalItems && limit ? Math.max(1, Math.ceil(totalItems / limit)) : 1);

      const currentPageMeta = meta.currentPage ?? meta.page ?? page;

      setPagination({
        totalInventory: totalItems || products.length,
        totalPages: totalPages || 1,
        currentPage: currentPageMeta || page,
      });
     
    } catch (err) {
      console.error("Search API error:", err?.response?.data || err?.message);
      setSearchResults([]);
      showInventoryError(err, "inventoryPage.errors.searchFilterFailed");
    }
    finally {
      setSearchLoading(false);
    }
  };

  // Map UI tab to API stockStatus param
  const mapActiveTabToStatus = (tab) => {
    switch (tab) {
      case "Out of Stock":
        return "out_of_stock";
      case "In Stock":
        return "in_stock";
      case "Low Stock":
        return "insufficient_stock";
      default:
        return "";
    }
  };

  // Unified fetcher for search + status filter, across all or a specific branch
  const fetchCombinedResults = async (
    page = 1,
    sortBy = inventorySortField,
    sortOrder = inventorySortDirection,
    limit = itemsPerPage
  ) => {
    try {
      const token = localStorage.getItem("token");
      const q = (debouncedSearchQuery || "").trim();
      const statusParam = mapActiveTabToStatus(activeTab);
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);
      if (q) params.set("productName", q);
      if (statusParam) params.set("stockStatus", statusParam);

      const path = selectedBranchId
        ? `/branches/${selectedBranchId}/products-with-inventory`
        : `/branches/all/products-with-inventory`;

      const response = await api.get(
        `${path}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const raw = response?.data;
      const products = Array.isArray(raw?.data?.products)
        ? raw.data.products
        : Array.isArray(raw?.products)
        ? raw.products
        : Array.isArray(raw)
        ? raw
        : [];

      setCombinedResults(products);

      const meta = (raw && raw.data && raw.data.pagination) || raw?.pagination || {};
      const totalItems =
        meta.totalItems ??
        meta.total ??
        (raw && raw.data && raw.data.totalProducts) ??
        (raw && raw.data && raw.data.total) ??
        products.length;

      const totalPages =
        meta.totalPages ??
        meta.pages ??
        meta.total_pages ??
        (totalItems && limit ? Math.max(1, Math.ceil(totalItems / limit)) : 1);

      const currentPageMeta = meta.currentPage ?? meta.page ?? page;

      setPagination({
        totalInventory: totalItems || products.length,
        totalPages: totalPages || 1,
        currentPage: currentPageMeta || page,
      });
     
    } catch (err) {
      console.error("Combined filter API error:", err?.response?.data || err?.message);
      setCombinedResults([]);
      showInventoryError(err, "inventoryPage.errors.searchFilterFailed");
    } finally {
      setListLoading(false);
    }
  };

  // Server-side status filter fetcher (across all branches), mirrors search normalization
  const fetchStatusResults = async (
    stockStatus,
    page = 1,
    limit = itemsPerPage
  ) => {
    try {
      setStatusLoading(true);
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (stockStatus) params.set("stockStatus", stockStatus);
      params.set("page", String(page));
      params.set("limit", String(limit));

      const response = await api.get(
        `/branches/all/products-with-inventory?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const raw = response?.data;
      const products = Array.isArray(raw?.data?.products)
        ? raw.data.products
        : Array.isArray(raw?.products)
        ? raw.products
        : Array.isArray(raw)
        ? raw
        : [];

      setStatusResults(products);

      const meta = (raw && raw.data && raw.data.pagination) || raw?.pagination || {};
      const totalItems =
        meta.totalItems ??
        meta.total ??
        (raw && raw.data && raw.data.totalProducts) ??
        (raw && raw.data && raw.data.total) ??
        products.length;

      const totalPages =
        meta.totalPages ??
        meta.pages ??
        meta.total_pages ??
        (totalItems && limit ? Math.max(1, Math.ceil(totalItems / limit)) : 1);

      const currentPageMeta = meta.currentPage ?? meta.page ?? page;

      setPagination({
        totalInventory: totalItems || products.length,
        totalPages: totalPages || 1,
        currentPage: currentPageMeta || page,
      });
      
    } catch (err) {
      console.error("Status filter API error:", err?.response?.data || err?.message);
      setStatusResults([]);
      showInventoryError(err, "inventoryPage.errors.searchFilterFailed");
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchApps = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const { apps: nextApps, addonApps: nextAddonApps } =
        await fetchPricedAppSources();
      setApps(nextApps);
      setAddonApps(nextAddonApps);
    } catch (error) {
      console.error(
        "Error fetching apps:",
        error.response?.data || error.message
      );
    }
  };

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Ensure response has the expected structure
      if (!response.data || !Array.isArray(response.data.stores)) {
        throw new Error("Invalid stores data in response");
      }

      const allStores = response.data.stores;
      setStores(allStores);

      // Process branches from all stores
      const allBranches = allStores.flatMap(store => {
        const storeBranches = store.branches || [];
        const storeName = store.name || 'Unknown Store';
        
        return storeBranches.map(branch => {
          const localizedBranchName = isArabic
            ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
            : branch?.name?.en || branch?.name;
          return {
            ...branch,
            storeId: store._id,
            storeName: storeName,
            displayName: localizedBranchName
              ? `${localizedBranchName} - (${storeName})`
              : `(${storeName})`
          };
        });
      });

      if (allBranches.length > 0) {
        setBranches(allBranches);
      } else {
        console.error("No branches found");
        toast.warn(t("inventoryPage.errors.noBranchesForStores"));
      }
    } catch (err) {
      console.error("Error fetching stores:", err.response?.data || err.message);
      toast.error(
        resolveInventoryPageError(err, t, "inventoryPage.errors.fetchStoresFailed")
      );
      setBranches([]);
    }
  };

  const fetchInventoryByBranch = async (
    branchId,
    page = 1,
    sortBy = inventorySortField,
    sortOrder = inventorySortDirection,
    tableOnly = false,
    limit = itemsPerPage
  ) => {
    if (tableOnly) {
      setListLoading(true);
    } else {
      setIsLoading(true);
    }
    try {
      const token = localStorage.getItem("token");
      
      // Fetch products with inventory status for this branch with server-side pagination
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("limit", String(limit));
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);

      const response = await api.get(
        `/branches/${branchId}/products-with-inventory?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
     
      if (response.data && response.data.success) {
        const { products } = response.data.data;
        
        // Transform products to inventory format for display
        const transformedInventory = products.map(product => {
          if (product.inv_found && product.inventory) {
            // Product has inventory - return inventory record format
            return {
              _id: product.inventory._id,
              product: {
                _id: product._id,
                product_id: product.product_id,
                name: product.name,
                description: product.description,
                price: product.price,
                app_prices: product.app_prices,
                image_path: product.image_path,
                category: product.category_id ? {
                  nameAr: product.category_id.name?.ar || '',
                  nameEn: product.category_id.name?.en || ''
                } : null
              },
              branch: {
                _id: branchId,
                name: (isArabic
                  ? (response.data.data.branch?.name?.ar || response.data.data.branch?.name_ar || response.data.data.branch?.name?.en || response.data.data.branch?.name)
                  : (response.data.data.branch?.name?.en || response.data.data.branch?.name))
              },
              date: product.inventory.date,
              quantity: product.inventory.quantity,
              initial_stock: product.inventory.initial_stock,
              current_stock: product.inventory.current_stock,
              low_stock_threshold: (product.inventory.low_stock_threshold ?? 5),
              is_below_threshold: product.inventory.is_below_threshold || false,
              updatedAt: product.inventory.updatedAt || product.inventory.updated_at || null
            };
          } else {
            // Product doesn't have inventory - return minimal format
            return {
              _id: null,
              product: {
                _id: product._id,
                product_id: product.product_id,
                name: product.name,
                description: product.description,
                price: product.price,
                app_prices: product.app_prices,
                image_path: product.image_path,
                category: product.category_id ? {
                  nameAr: product.category_id.name?.ar || '',
                  nameEn: product.category_id.name?.en || ''
                } : null
              },
              branch: {
                _id: branchId,
                name: (isArabic
                  ? (response.data.data.branch?.name?.ar || response.data.data.branch?.name_ar || response.data.data.branch?.name?.en || response.data.data.branch?.name)
                  : (response.data.data.branch?.name?.en || response.data.data.branch?.name))
              },
              date: selectedDate ? new Date(selectedDate) : new Date(),
              quantity: 0,
              initial_stock: 0,
              current_stock: 0,
              low_stock_threshold: 0,
              is_below_threshold: true,
              inv_found: false
            };
          }
        });
        
        setInventory(transformedInventory);
        // Derive pagination meta from response if available
        const meta =
          (response.data.data && response.data.data.pagination) ||
          response.data.pagination || {};

        const totalItems =
          meta.totalItems ??
          meta.total ??
          (response.data.data && response.data.data.totalProducts) ??
          (response.data.data && response.data.data.total) ??
          transformedInventory.length;

        const totalPages =
          meta.totalPages ??
          meta.pages ??
          meta.total_pages ??
          (totalItems && limit ? Math.max(1, Math.ceil(totalItems / limit)) : 1);

        const currentPageMeta = meta.currentPage ?? meta.page ?? page;

        setPagination({
          totalInventory: totalItems,
          totalPages: totalPages || 1,
          currentPage: currentPageMeta || page,
        });
        // Also persist branch pagination for restoring after search clears
        setBranchPagination({
          totalInventory: totalItems,
          totalPages: totalPages || 1,
          currentPage: currentPageMeta || page,
        });
      }
    } catch (err) {
      console.error("Error fetching inventory by branch:", err.response?.data || err.message);
      toast.error(
        resolveInventoryPageError(err, t, "inventoryPage.errors.fetchInventoryFailed")
      );
    } finally {
      if (tableOnly) {
        setListLoading(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleBranchSelect = (branch) => {
    const id = branch && branch._id ? branch._id : "";
    if (id) {
      setSelectedBranch(branch);
      console.log("Selected branch:", branch);
      setSelectedBranchId(id);
      setTenantApiScope(branch.storeId || branch.store_id || null, id);
      fetchInventoryByBranch(id);
    } else {
      // Clear selection when dropdown is cleared
      setSelectedBranch(null);
      setSelectedBranchId(null);
      setTenantApiScope(null, null);
      setInventory([]);
      setFilteredInventory([]);
      setCombinedResults([]);
      setListLoading(false);
      setPagination({ totalInventory: 0, totalPages: 1, currentPage: 1 });
    }
  };

  const handleInventoryEnabledToggle = async (checked) => {
    if (!canEnableDisableInventory) {
      toast.error(enableDisableInventoryTip);
      return;
    }
    const prev = inventoryEnabled;
    setInventoryEnabled(checked);
    if (!selectedBranchId) {
      toast.error(t("selectBranchFirst"));
      setInventoryEnabled(prev);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await api.post(`/branch/${selectedBranchId}/inventory-enabled`, { inventory_enabled: checked }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedBranch((prevBranch) => prevBranch ? { ...prevBranch, inventory_enabled: checked } : prevBranch);
      setBranches((prevBranches) => prevBranches.map((b) => (
        b._id === selectedBranchId ? { ...b, inventory_enabled: checked } : b
      )));
      toast.success(
        checked
          ? t("inventoryPage.toasts.inventoryEnabled")
          : t("inventoryPage.toasts.inventoryDisabled")
      );
    } catch (err) {
      console.error("Error toggling inventory_enabled:", err.response?.data || err.message);
      showInventoryError(err, "inventoryPage.errors.toggleSettingFailed");
      setInventoryEnabled(prev);
    }
  };

  // Client-side search handlers removed

  const toggleEditModal = (item = null) => {
    setEditItem(item);
    setEditModal(!editModal);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const renderModalSaveButton = () => {
    const disabled = updateLoading || !canMutateInventory;
    const btn = (
      <Button
        type="button"
        color="primary"
        onClick={handleUpdate}
        disabled={disabled}
        style={!canMutateInventory ? { pointerEvents: "none" } : undefined}
      >
        {updateLoading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
            {t("saving")}...
          </>
        ) : (
          t("savechanges")
        )}
      </Button>
    );
    if (!canMutateInventory) {
      return (
        <MDBTooltip tag="span" title={mutateInventoryTip} placement="top">
          <span style={{ display: "inline-block" }}>{btn}</span>
        </MDBTooltip>
      );
    }
    return btn;
  };

  const handleUpdate = async () => {
    if (!canMutateInventory) {
      toast.error(mutateInventoryTip);
      return;
    }
    try {
      setUpdateLoading(true);
      const token = localStorage.getItem("token");
      await api.put(`/inventory/v2/${editItem._id}`, editItem, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toggleEditModal();
      const updatedAt = new Date().toISOString();
      const nextStock = parseInt(editItem.current_stock ?? editItem.quantity ?? 0, 10);
      const nextThreshold = parseInt(editItem.low_stock_threshold ?? 5, 10);
      setInventory(prev => prev.map(row => (
        row._id === editItem._id
          ? {
              ...row,
              current_stock: isNaN(nextStock) ? (row.current_stock || 0) : nextStock,
              low_stock_threshold: isNaN(nextThreshold) ? (row.low_stock_threshold ?? 5) : nextThreshold,
              is_below_threshold: (!isNaN(nextStock) && !isNaN(nextThreshold)) ? (nextStock < nextThreshold) : row.is_below_threshold,
              updatedAt,
            }
          : row
      )));
    } catch (err) {
      console.error("Error updating inventory item:", err.response?.data || err.message);
      showInventoryError(err, "inventoryPage.errors.updateItemFailed");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("Are you sure you want to delete this item?")))
      return;
    try {
      const token = localStorage.getItem("token");
      await api.delete(`/inventory/v2/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (selectedBranchId) {
        fetchInventoryByBranch(selectedBranchId, pagination.currentPage);
      }
    } catch (err) {
      console.error("Error deleting inventory item:", err.response?.data || err.message);
      showInventoryError(err, "inventoryPage.errors.deleteItemFailed");
    }
  };

  const handlePageChange = (page) => {
    const q = (debouncedSearchQuery || "").trim();
    const hasFilter = activeTab && activeTab !== "All";
    if (q || hasFilter) {
      fetchCombinedResults(page);
      return;
    }
    if (selectedBranchId) {
      fetchInventoryByBranch(selectedBranchId, page, undefined, undefined, true);
    }
  };

  const handlePageSizeChange = (size) => {
    setItemsPerPage(size);
    const q = (debouncedSearchQuery || "").trim();
    const hasFilter = activeTab && activeTab !== "All";
    if (q || hasFilter) {
      setListLoading(true);
      fetchCombinedResults(1, inventorySortField, inventorySortDirection, size);
    } else if (selectedBranchId) {
      fetchInventoryByBranch(
        selectedBranchId,
        1,
        inventorySortField,
        inventorySortDirection,
        true,
        size
      );
    }
  };

  const applyTableSort = (column, direction) => {
    const apiField = INVENTORY_TABLE_SORT_FIELDS[column];
    if (!apiField) return;

    setTableSortColumn(column);
    setInventorySortField(apiField);
    setInventorySortDirection(direction);

    const q = (debouncedSearchQuery || "").trim();
    const hasFilter = activeTab && activeTab !== "All";
    if (q || hasFilter) {
      setListLoading(true);
      fetchCombinedResults(1, apiField, direction);
    } else if (selectedBranchId) {
      fetchInventoryByBranch(selectedBranchId, 1, apiField, direction, true);
    }
  };

  // Handle inline edit - start editing a row
  const handleStartEdit = (item) => {
    const rowId = item._id || `new-${item.product?._id}`;
    setEditingRow(rowId);
    setEditingData(prev => ({
      ...prev,
      [rowId]: {
        current_stock: item.current_stock || 0,
        low_stock_threshold: (item.low_stock_threshold ?? 0)
      }
    }));
  };

  // Handle inline edit - cancel editing
  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditingData({});
  };

  // Handle inline edit - update field value (track per-row)
  const handleInlineFieldChange = (rowId, field, value) => {
    // Preserve empty string while typing; convert to number otherwise
    const nextVal = value === '' ? '' : Math.max(0, parseInt(value, 10));
    setEditingData(prev => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [field]: nextVal,
      },
    }));
  };

  // Handle inline save
  const handleInlineSave = async (item) => {
    if (!canMutateInventory) {
      toast.error(mutateInventoryTip);
      return;
    }
    const rowId = item._id || `new-${item.product?._id}`;
    const rowEdit = editingData[rowId] || {};
    const current = (rowEdit.current_stock ?? item.current_stock ?? 0);
    const threshold = (rowEdit.low_stock_threshold ?? item.low_stock_threshold ?? 5);
    const productName = productDisplayName(item);

    if (current === '' || threshold === '' || current === null || threshold === null || current === undefined || threshold === undefined) {
      toast.error(t("inventoryPage.errors.inlineEnterStockAndThreshold", { product: productName }));
      return;
    }

    // If item doesn't have an _id, it means inventory doesn't exist yet - need to create it
    if (!item._id) {
      try {
        setSavingRowId(rowId);
        const token = localStorage.getItem("token");
        const inventoryData = {
          product: item.product._id,
          branch: selectedBranchId,
          date: selectedDate || new Date().toISOString().split("T")[0],
          quantity: current || 0,
          initial_stock: current || 0,
          current_stock: current || 0,
          low_stock_threshold: threshold,
        };

        const res = await api.post('/inventory', inventoryData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(t("inventoryPage.errors.inlineCreateSuccess", { product: productName }));
        const created = (res && res.data && (res.data.inventory || res.data.data?.inventory || res.data)) || null;
        const newId = created && created._id ? created._id : null;
        const updatedAt = new Date().toISOString();
        setInventory(prev => prev.map(row => {
          const matches = (!row._id && (row.product?._id === item.product?._id)) || (row._id === item._id);
          if (matches) {
            return {
              ...row,
              _id: newId || row._id || `tmp-${row.product?._id}`,
              current_stock: current || 0,
              low_stock_threshold: threshold,
              is_below_threshold: (current || 0) < (threshold || 0),
              updatedAt,
              inv_found: true,
            };
          }
          return row;
        }));
        if ((debouncedSearchQuery || '').trim().length > 0 || (activeTab && activeTab !== 'All')) {
          setCombinedResults(prev => prev.map(p => {
            const pid = p?.product?._id || p?._id;
            if (pid === (item.product?._id)) {
              const inv = {
                ...(p.inventory || {}),
                current_stock: current || 0,
                low_stock_threshold: threshold,
                updatedAt,
                is_below_threshold: (current || 0) < (threshold || 0),
                _id: newId || item._id || `tmp-${pid}`,
                date: p.inventory?.date || (selectedDate ? new Date(selectedDate) : new Date()),
                branch_id: selectedBranchId,
              };
              return { ...p, inv_found: true, inventory: inv };
            }
            return p;
          }));
        }
        // Clear edited data for this row
        setEditingData(prev => { const next = { ...prev }; delete next[rowId]; return next; });
      } catch (err) {
        console.error("Error creating inventory:", err.response?.data || err.message);
        showInventoryError(err, "inventoryPage.errors.inlineCreateFailed", productName);
      } finally {
        setSavingRowId(null);
      }
    } else {
      // Update existing inventory
      try {
        setSavingRowId(rowId);
        const token = localStorage.getItem("token");
        const updateData = {
          current_stock: current || 0,
          low_stock_threshold: threshold ,
          reason: 'Inventory updated via inline edit',
          type: 'manual',
        };

        await api.put(`/inventory/v2/${item._id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const stockChanged = Object.prototype.hasOwnProperty.call(rowEdit, 'current_stock') && (parseInt(rowEdit.current_stock ?? 0, 10) !== parseInt(item.current_stock ?? 0, 10));
        const thresholdChanged = Object.prototype.hasOwnProperty.call(rowEdit, 'low_stock_threshold') && (parseInt(rowEdit.low_stock_threshold ?? 0, 10) !== parseInt(item.low_stock_threshold ?? 0, 10));
        if (stockChanged && thresholdChanged) {
          toast.success(t("inventoryPage.errors.inlineStockThresholdSuccess", { product: productName }));
        } else if (stockChanged) {
          toast.success(t("inventoryPage.errors.inlineStockSuccess", { product: productName }));
        } else if (thresholdChanged) {
          toast.success(t("inventoryPage.errors.inlineThresholdSuccess", { product: productName }));
        } else {
          toast.success(t("inventoryPage.errors.inlineUpdateSuccess", { product: productName }));
        }
        const updatedAt = new Date().toISOString();
        setInventory(prev => prev.map(row => (
          row._id === item._id
            ? {
                ...row,
                current_stock: current || 0,
                low_stock_threshold: threshold,
                is_below_threshold: (current || 0) < (threshold || 0),
                updatedAt,
              }
            : row
        )));
        if ((debouncedSearchQuery || '').trim().length > 0 || (activeTab && activeTab !== 'All')) {
          setCombinedResults(prev => prev.map(p => {
            const pid = p?.product?._id || p?._id;
            if (pid === (item.product?._id)) {
              const inv = {
                ...(p.inventory || {}),
                current_stock: current || 0,
                low_stock_threshold: threshold,
                updatedAt,
                is_below_threshold: (current || 0) < (threshold || 0),
                _id: item._id,
                date: p.inventory?.date || (selectedDate ? new Date(selectedDate) : new Date()),
                branch_id: selectedBranchId,
              };
              return { ...p, inv_found: true, inventory: inv };
            }
            return p;
          }));
        }
        // Clear edited data for this row
        setEditingData(prev => { const next = { ...prev }; delete next[rowId]; return next; });
      } catch (err) {
        console.error("Error updating inventory:", err.response?.data || err.message);
        showInventoryError(err, "inventoryPage.errors.inlineUpdateFailed", productName);
      } finally {
        setSavingRowId(null);
      }
    }
  };

  // Derived inventory to display: combined results when search or status filter active
  const isSearchMode = (debouncedSearchQuery || "").trim().length > 0;
  const isStatusFilterMode = activeTab && activeTab !== "All";
  const isCombinedMode = isSearchMode || isStatusFilterMode;
  const displayedInventory = isCombinedMode
    ? transformProductsToInventory(combinedResults)
    : filteredInventory;

  const getInventoryRowId = useCallback(
    (item) => item._id || `new-${item.product?._id}`,
    []
  );

  const inventoryItemHasEditsToSave = useCallback(
    (item) => {
      const rowId = getInventoryRowId(item);
      const rowEditLocal = editingData[rowId] || {};
      const currentStock = editingData[rowId]?.current_stock ?? (item.current_stock || 0);
      const stockThreshold =
        editingData[rowId]?.low_stock_threshold ?? (item.low_stock_threshold ?? 0);
      const hasCurrentEdit =
        Object.prototype.hasOwnProperty.call(rowEditLocal, "current_stock") &&
        parseInt(rowEditLocal.current_stock ?? 0, 10) !==
          parseInt(item.current_stock ?? 0, 10);
      const hasThresholdEdit =
        Object.prototype.hasOwnProperty.call(rowEditLocal, "low_stock_threshold") &&
        parseInt(rowEditLocal.low_stock_threshold ?? 0, 10) !==
          parseInt(item.low_stock_threshold ?? 0, 10);
      const isEmptyCurrent =
        currentStock === "" || currentStock === null || currentStock === undefined;
      const isEmptyThreshold =
        stockThreshold === "" || stockThreshold === null || stockThreshold === undefined;
      return (
        !isEmptyCurrent && !isEmptyThreshold && (hasCurrentEdit || hasThresholdEdit)
      );
    },
    [editingData, getInventoryRowId]
  );

  const handleBulkSave = useCallback(
    async (selectedRowIds) => {
      if (!canMutateInventory) {
        toast.error(mutateInventoryTip);
        return;
      }
      const idSet = new Set((selectedRowIds || []).map(String));
      const itemsToSave = displayedInventory.filter((item) =>
        idSet.has(String(getInventoryRowId(item)))
      );
      const withEdits = itemsToSave.filter(inventoryItemHasEditsToSave);
      if (withEdits.length === 0) return;
      for (const item of withEdits) {
        await handleInlineSave(item);
      }
      setSelectedIds([]);
    },
    [
      canMutateInventory,
      mutateInventoryTip,
      displayedInventory,
      getInventoryRowId,
      inventoryItemHasEditsToSave,
      handleInlineSave,
    ]
  );

  const inventoryEmptyState = useMemo(() => {
    if (isCombinedMode && listLoading) {
      return isSearchMode ? t("Searching...") : t("Filtering...");
    }
    return selectedBranchId ? t("noProductsToDisplay") : t("noProductsSelectBranch");
  }, [isCombinedMode, listLoading, isSearchMode, selectedBranchId, t]);

  const inventoryColumns = useMemo(
    () => {
      const productImageHeader = t("productimage");
      // Fixed px (not %): with table-layout:fixed, % widths shrink below the
      // label on small screens even when minWidth is set.
      const productImageWidth = Math.max(
        150,
        estimateColumnMinWidth(productImageHeader)
      );
      return [
      {
        id: "image",
        header: productImageHeader,
        hideable: false,
        width: productImageWidth,
        minWidth: productImageWidth,
        align: "center",
        render: (item) => (
          <img
            src={getImageUrl(item.product?.image_path)}
            alt={
              (isArabic
                ? item.product?.name?.ar || item.product?.name?.en
                : item.product?.name?.en || item.product?.name?.ar) || ""
            }
            className="inventory-product-thumb"
            onError={(e) => {
              if (e.target.src && !e.target.src.startsWith("data:")) {
                e.target.src =
                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='40' height='40' fill='%23ddd'/%3E%3Ctext x='50%25' y='50%25' font-size='10' fill='%23999' text-anchor='middle' dy='.3em'%3E—%3C/text%3E%3C/svg%3E";
                e.target.onerror = null;
              }
            }}
          />
        ),
      },
      {
        id: "name",
        header: t("productname"),
        strong: true,
        sortable: true,
        width: "24%",
        minWidth: 200,
        nowrap: true,
        render: (item) => {
          const displayName = isArabic
            ? item.product?.name?.ar || item.product?.name?.en || ""
            : item.product?.name?.en || item.product?.name?.ar || "";
          return (
            <TableCellEllipsis title={displayName}>{displayName}</TableCellEllipsis>
          );
        },
      },
      {
        id: "category",
        header: t("category"),
        sortable: true,
        width: "14%",
        minWidth: 130,
        nowrap: true,
        render: (item) => {
          const categoryName = isArabic
            ? item.product?.category?.nameAr || item.product?.category?.nameEn
            : item.product?.category?.nameEn || item.product?.category?.nameAr;
          return (
            <TableCellEllipsis title={categoryName}>{categoryName}</TableCellEllipsis>
          );
        },
      },
      {
        id: "price",
        header: t("price"),
        sortable: true,
        width: "9%",
        minWidth: 95,
        align: "end",
        render: (item) => (
          <ProductPriceWithApps
            product={item.product}
            deliveryApps={deliveryApps}
            iconSize={12}
            color="#030f1b"
          />
        ),
      },
      {
        id: "current_stock",
        header: t("availablestock"),
        sortable: true,
        width: "12%",
        minWidth: 125,
        align: "end",
        render: (item) => {
          const rowId = getInventoryRowId(item);
          const isSaving = savingRowId === rowId;
          const currentStock =
            editingData[rowId]?.current_stock ?? (item.current_stock || 0);
          const stockThreshold =
            editingData[rowId]?.low_stock_threshold ?? (item.low_stock_threshold ?? 0);
          return (
            <input
              type="number"
              min="0"
              step="1"
              value={currentStock}
              onChange={(e) =>
                handleInlineFieldChange(rowId, "current_stock", e.target.value)
              }
              className={`form-control inventory-inline-input ${
                currentStock < stockThreshold ? "inventory-inline-input-danger" : ""
              }`}
              disabled={isSaving || !canMutateInventory}
            />
          );
        },
      },
      {
        id: "low_stock_threshold",
        header: t("stockthreshold"),
        sortable: true,
        width: "12%",
        minWidth: 130,
        align: "end",
        render: (item) => {
          const rowId = getInventoryRowId(item);
          const isSaving = savingRowId === rowId;
          const stockThreshold =
            editingData[rowId]?.low_stock_threshold ?? (item.low_stock_threshold ?? 0);
          return (
            <input
              type="number"
              min="0"
              step="1"
              value={stockThreshold}
              onChange={(e) =>
                handleInlineFieldChange(rowId, "low_stock_threshold", e.target.value)
              }
              className="form-control inventory-inline-input"
              disabled={isSaving || !canMutateInventory}
            />
          );
        },
      },
      {
        id: "updatedAt",
        header: t("lastUpdated"),
        sortable: true,
        width: "16%",
        minWidth: 155,
        render: (item) =>
          item.updatedAt
            ? new Date(item.updatedAt)
                .toLocaleString("en-GB", {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })
                .replace(/am|pm/g, (match) => match.toUpperCase())
            : "-",
      },
      {
        id: "actions",
        header: t("actions"),
        width: "8%",
        minWidth: 100,
        align: "center",
        render: (item) => {
          const rowId = getInventoryRowId(item);
          const isSaving = savingRowId === rowId;
          const saveDisabled =
            isSaving || !canMutateInventory || !inventoryItemHasEditsToSave(item);
          const inlineSaveBtn = (
            <Button
              className="inventory-save-btn"
              color="success"
              outline
              size="sm"
              onClick={() => handleInlineSave(item)}
              disabled={saveDisabled}
              style={{
                minWidth: "70px",
                cursor: saveDisabled ? "not-allowed" : "pointer",
                pointerEvents: saveDisabled && !canMutateInventory ? "none" : "auto",
              }}
            >
              {isSaving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  {t("Saving")}
                </>
              ) : (
                t("save")
              )}
            </Button>
          );
          return (
            <div className="d-flex gap-2">
              {!canMutateInventory ? (
                <MDBTooltip tag="span" title={mutateInventoryTip} placement="top">
                  <span style={{ display: "inline-block" }}>{inlineSaveBtn}</span>
                </MDBTooltip>
              ) : (
                inlineSaveBtn
              )}
            </div>
          );
        },
      },
    ];
    },
    [
      t,
      isArabic,
      editingData,
      savingRowId,
      canMutateInventory,
      mutateInventoryTip,
      getInventoryRowId,
      inventoryItemHasEditsToSave,
      handleInlineFieldChange,
      handleInlineSave,
      deliveryApps,
    ]
  );

  const inventoryBulkActions = useMemo(
    () => [
      {
        label: t("Save Changes"),
        onClick: handleBulkSave,
      },
    ],
    [t, handleBulkSave]
  );

  return (
    <div className="inventory-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        {isLoading ? (
          <MDBContainer fluid className="content-area flex-grow-1">
            {/* Large header skeleton */}
            <Skeleton height={40} width={220} style={{ marginBottom: 32 }} />
            {/* Row of stat card skeletons */}
            <MDBRow className="mb-4">
              {Array.from({ length: 2 }).map((_, i) => (
                <MDBCol xs="12" sm="6" md="4" className="mb-4" key={i}>
                  <Skeleton height={100} borderRadius={12} />
                </MDBCol>
              ))}
            </MDBRow>
            {/* Filters and actions skeletons */}
            <MDBRow className="mb-4">
              <MDBCol md="2">
                <Skeleton
                  height={36}
                  width={180}
                  style={{ marginBottom: 16 }}
                />
              </MDBCol>
              <MDBCol md="5">
                <Skeleton
                  height={36}
                  width={240}
                  style={{ marginBottom: 16 }}
                />
              </MDBCol>
              <MDBCol md="5">
                <Skeleton
                  height={36}
                  width={240}
                  style={{ marginBottom: 16 }}
                />
              </MDBCol>
            </MDBRow>
            {/* Table skeleton */}
            <MDBRow>
              <MDBCol md="12">
                <Skeleton
                  height={48}
                  style={{ marginBottom: 8, borderRadius: 8 }}
                  count={8}
                />
              </MDBCol>
            </MDBRow>
          </MDBContainer>
        ) : (
          <>
            <h1 className="page-title-header">
              {t("inventoryPage.pageTitle")}
            </h1>

            <div className="oh-page-section">
              <Toolbar
                showBranchDropdown={true}
                options={branches}
                handleSelect={handleBranchSelect}
                selectedOption={selectedBranch}
                placeholder={t("Select Branch")}
                branchLabel="branch"
                backgroundColor="#f9f9f9"
                showFilter={true}
                orderStatuses={[
                  { id: "All", name: `${t("all")}` },
                  { id: "In Stock", name: `${t("instock")}` },
                  { id: "Out of Stock", name: `${t("outofstock")}` },
                  { id: "Low Stock", name: `${t("lowstock")}` },
                ]}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                showSort={false}
                showSearch={true}
                searchQuery={searchQuery}
                handleSearch={handleSearch}
                showDateFilter={false}
                selectedDate={selectedDate}
                handleDateChange={(e) => setSelectedDate(e.target.value)}
                showRightToggle={true}
                rightToggleChecked={inventoryEnabled}
                rightToggleOnChange={handleInventoryEnabledToggle}
                rightToggleTitle={t("linkInventoryToOrderAutoAccept")}
                rightToggleSubtitle={inventoryEnabled
                  ? t("check_inventory_before_accepting")
                  : t("ignore_inventory_before_accepting")}
                rightToggleDisabled={
                  !canEnableDisableInventory ||
                  !selectedBranch ||
                  selectedBranch?.is_foodics_branch === true
                }
                rightToggleTooltip={
                  !canEnableDisableInventory
                    ? enableDisableInventoryTip
                    : !selectedBranch
                      ? t("inventoryPage.tooltips.selectBranchFirst")
                      : selectedBranch?.is_foodics_branch === true
                        ? t("inventoryPage.tooltips.foodicsBranch")
                        : ""
                }
              />
            </div>
            <AddInventoryModal
              show={showModal}
              setShow={setShowModal}
              
            />

            <DataTable
              tableId="inventory"
              title={t("inventoryPage.pageTitle")}
              className="inventory-table"
              bulkBarPlacement="end"
              selectable
              selectionMode="checkbox"
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              columns={inventoryColumns}
              data={displayedInventory}
              rowKey={getInventoryRowId}
              loading={listLoading || isLoading}
              emptyState={inventoryEmptyState}
              bulkActions={inventoryBulkActions}
              actionDropdownLabel={t("Bulk Action", { defaultValue: "Bulk Action" })}
              selectedLabel={t("Selected", { defaultValue: "Selected" })}
              selectAllLabel={t("Select All", { defaultValue: "Select All" })}
              isRtl={isArabic}
              sort={{
                column: tableSortColumn,
                direction: inventorySortDirection,
                onChange: ({ column, direction }) =>
                  applyTableSort(column, direction),
              }}
              pagination={
                !listLoading && pagination.totalInventory > 0
                  ? {
                      page: pagination.currentPage,
                      pageSize: itemsPerPage,
                      total: pagination.totalInventory,
                      onPageChange: handlePageChange,
                      onPageSizeChange: handlePageSizeChange,
                      pageSizeOptions: [10, 20, 25, 50],
                      showLabel: t("Show", { defaultValue: "Show" }),
                      ofLabel: t("of", { defaultValue: "Of" }),
                      resultsLabel: t("results", { defaultValue: "results" }),
                      isRtl: isArabic,
                      previousLabel: t("previous"),
                      nextLabel: t("next"),
                    }
                  : undefined
              }
            />

            {editItem && (
              <div
                className={`modal ${editModal ? "show d-block" : ""}`}
                tabIndex="-1"
              >
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5>{t("Edit Inventory Item")}</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={toggleEditModal}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <NativeInput
                        label={t("Product Name")}
                        name="product_name"
                        value={editItem.product_name || ""}
                        onChange={handleEditChange}
                        className="mb-3"
                      />
                      <NativeInput
                        label={t("category")}
                        name="category"
                        value={editItem.category || ""}
                        onChange={handleEditChange}
                        className="mb-3"
                      />
                      <NativeInput
                        label={t("quantity")}
                        name="quantity"
                        type="number"
                        value={editItem.quantity || ""}
                        onChange={handleEditChange}
                        className="mb-3"
                      />
                      <NativeInput
                        label={t("price")}
                        name="price"
                        type="number"
                        value={editItem.price || ""}
                        onChange={handleEditChange}
                        className="mb-3"
                      />
                      <NativeInput
                        label={t("supplier")}
                        name="supplier"
                        value={editItem.supplier || ""}
                        onChange={handleEditChange}
                        className="mb-3"
                      />
                    </div>
                    {isArabic ? (
                      <div className="modal-footer">
                        {renderModalSaveButton()}
                        <Button type="button" color="secondary" onClick={toggleEditModal}>
                          {t("close")}
                        </Button>
                      </div>
                    ) : (
                      <div className="modal-footer">
                        <Button type="button" color="secondary" onClick={toggleEditModal}>
                          {t("close")}
                        </Button>
                        {renderModalSaveButton()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </MDBContainer>
    </div>
  );
}

export default InventoryPage;
